import { createEffect, createEvent, createStore, sample } from "effector";
import { ModelContract, ModuleDescription } from "../../shared/module-description";
import { IDailyTaskContract } from "../model";
import { reset } from 'patronum';
import { IUserAuthContract } from '@features/student/auth/model';
import { IMetadataContract } from '@features/student/metadata/model';

import { IFlagsmithContract } from "@features/flagsmith/model";

const onboardingLocalStorageKey = "dailyTaskOnboardingCompleted";

function userSeenOnboarding() {
    if (typeof window === "undefined") return true;
    const v = Boolean(localStorage.getItem("dailyTaskOnboardingCompleted"));
    return v;
}
// SECTION 1: UI EVENTS - Widget-specific events for handling UI interactions
export const showDailyTaskCompletePopup = createEvent();
export const hideDailyTaskCompletePopup = createEvent();
export const hideDailyTaskOnboarding = createEvent();
export const userPromptInputChanged = createEvent<string>();
export const submitPromptClicked = createEvent();
export const continueToNextTaskClicked = createEvent();
export const backToHomeClicked = createEvent();
const tryAgainClicked = createEvent();

// SECTION 2: UI STORES - Local widget state management
export const $dailyTaskCompletePopupVisible = createStore(false)
    .on(showDailyTaskCompletePopup, () => true)
    .on(hideDailyTaskCompletePopup, () => false);

export const $dailyTaskOnboardingVisible = createStore(false)
    .on(hideDailyTaskOnboarding, () => false);

export const $userPromptInput = createStore<string>("").on(userPromptInputChanged, (_, value) => value);

const $widgetType = createStore<"small" | "big">("big");
// .reset([submitPromptClicked, continueToNextTaskClicked]);

export const $isSubmitEnabled = $userPromptInput.map((prompt) => prompt.trim().length > 0);


// SECTION 3: UI CONTRACT
const contract = new ModelContract(
    {
        $dailyTaskCompletePopupVisible,
        $dailyTaskOnboardingVisible,
        $userPromptInput,
        $isSubmitEnabled,
        $widgetType
    },
    {
        showDailyTaskCompletePopup,
        hideDailyTaskCompletePopup,
        hideDailyTaskOnboarding,
        userPromptInputChanged,
        submitPromptClicked,
        continueToNextTaskClicked,
        backToHomeClicked,
        tryAgainClicked
    },
    {},
    {}
);

export type IDailyTaskUiContract = typeof contract;

export class DailyTaskUiModule extends ModuleDescription<IDailyTaskUiContract> {
    constructor(public dailyTaskModule: IDailyTaskContract, public userAuthModule: IUserAuthContract, public studentMetadata: IMetadataContract, public flagsmithModule: IFlagsmithContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            source: this.studentMetadata.stores.$metadata,
            filter: (m) => m[onboardingLocalStorageKey] !== 'true',
            fn: () => true,
            target: $dailyTaskOnboardingVisible
        });

        sample({
            clock: hideDailyTaskOnboarding,
            fn: () => ({
                key: onboardingLocalStorageKey,
                value: 'true'
            }),
            target: this.studentMetadata.events.setMetadataRequested
        });

        // Handle prompt submission
        sample({
            clock: submitPromptClicked,
            source: {
                prompt: $userPromptInput,
                task: this.dailyTaskModule.stores.$dailyTask
            },
            filter: ({ prompt, task }) => (prompt.trim().length > 0 && task !== null),
            fn: ({ prompt, task }) => ({
                task_id: task!.id,
                user_input: prompt
            }),
            target: this.dailyTaskModule.effects.dailyTaskRespondFx
        });

        // Track prompt submission for analytics
        sample({
            clock: submitPromptClicked,
            source: {
                prompt: $userPromptInput,
                task: this.dailyTaskModule.stores.$dailyTask
            },
            filter: ({ prompt, task }) => prompt.trim().length > 0 && task !== null,
            fn: ({ prompt, task }) => ({
                task_id: task!.id,
                prompt: prompt
            }),
            target: this.dailyTaskModule.events.dailyTaskPromptSent
        });

        // Add user message to history
        sample({
            clock: submitPromptClicked,
            source: $userPromptInput,
            filter: (prompt) => prompt.trim().length > 0,
            target: this.dailyTaskModule.events.addUserMessageToTaskResponseHistory
        });

        // After getting AI response, validate the prompt
        sample({
            clock: this.dailyTaskModule.effects.dailyTaskRespondFx.doneData,
            source: {
                prompt: $userPromptInput,
                task: this.dailyTaskModule.stores.$dailyTask
            },
            filter: ({ task }) => task !== null,
            fn: ({ prompt, task }) => ({
                user_prompt: prompt,
                task_id: task!.id
            }),
            target: this.dailyTaskModule.effects.dailyTaskValidateFx
        });

        // Reset UI state when starting a new task session
        sample({
            clock: this.dailyTaskModule.events.resetDailyTaskSession,
            target: hideDailyTaskCompletePopup
        });

        reset({
            clock: this.dailyTaskModule.events.resetDailyTaskSession,
            target: $userPromptInput
        });

        sample({
            clock: this.dailyTaskModule.events.dailyTaskTryAgain,
            target: hideDailyTaskCompletePopup
        });

        sample({
            clock: tryAgainClicked,
            target: this.dailyTaskModule.events.dailyTaskTryAgain
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: [
                $dailyTaskOnboardingVisible,
                $userPromptInput,
                $dailyTaskCompletePopupVisible
            ]
        });
    }
}
